<template>
  <div
    class="app-data-table"
    :class="{
      'app-data-table--refreshing': loading && rows.length,
      'app-data-table--comfortable': density === 'comfortable',
      'app-data-table--dense': density === 'dense'
    }"
    :aria-busy="loading ? 'true' : 'false'"
  >
    <table v-if="rows.length || loading" :style="{ minWidth }">
      <colgroup>
        <col
          v-for="column in columns"
          :key="column.key"
          :style="columnStyle(column)"
        >
      </colgroup>
      <thead>
        <tr>
          <th
            v-for="column in columns"
            :key="column.key"
            scope="col"
            :class="columnClass(column)"
            :style="cellStyle(column)"
            :aria-sort="ariaSort(column)"
          >
            <slot :name="`header-${column.key}`" :column="column">
              <button
                v-if="column.sortable"
                type="button"
                class="app-data-table__sort"
                :class="{ 'app-data-table__sort--active': sortDirection(column) }"
                @click="toggleSort(column)"
              >
                <span>{{ column.label }}</span>
                <span class="app-data-table__sort-icon" :class="`app-data-table__sort-icon--${sortDirection(column) || 'none'}`" aria-hidden="true" />
              </button>
              <template v-else>
                {{ column.label }}
              </template>
            </slot>
          </th>
        </tr>
      </thead>
      <tbody>
        <template v-if="loading && !rows.length">
          <tr v-for="rowNumber in skeletonRows" :key="`skeleton-${rowNumber}`" aria-hidden="true">
            <td v-for="column in columns" :key="column.key">
              <span class="app-data-table__skeleton" />
            </td>
          </tr>
        </template>
        <template v-else>
          <template v-for="(row, index) in rows">
          <tr
            :key="rowIdentity(row, index)"
            :class="{ 'app-data-table__row--clickable': clickable }"
            :tabindex="clickable ? 0 : null"
            :aria-expanded="isRowExpanded ? String(isRowExpanded(row)) : null"
            @click="selectRow(row, $event)"
            @keydown.enter="selectRowByKey(row, $event)"
            @keydown.space="selectRowByKey(row, $event)"
          >
            <td
              v-for="column in columns"
              :key="column.key"
              :class="columnClass(column)"
              :style="cellStyle(column)"
            >
              <slot
                :name="`cell-${column.key}`"
                :row="row"
                :column="column"
                :value="row[column.key]"
              >
                <slot
                  name="cell"
                  :row="row"
                  :column="column"
                  :value="row[column.key]"
                >
                  <span class="app-data-table__ellipsis">
                    {{ displayValue(row[column.key]) }}
                  </span>
                </slot>
              </slot>
            </td>
          </tr>
          <tr v-if="isRowExpanded && isRowExpanded(row)" :key="`${rowIdentity(row, index)}:detail`" class="app-data-table__detail-row">
            <td :colspan="columns.length"><slot name="row-detail" :row="row" /></td>
          </tr>
          </template>
        </template>
      </tbody>
    </table>
    <slot v-else name="empty" />
  </div>
</template>

<script>
export default {
  name: 'AppDataTable',
  props: {
    design: {
      type: String,
      required: true,
      validator: value => value === 'new'
    },
    columns: {
      type: Array,
      required: true
    },
    rows: {
      type: Array,
      default: () => []
    },
    rowKey: {
      type: [String, Function],
      default: 'id'
    },
    minWidth: {
      type: String,
      default: '1200px'
    },
    density: {
      type: String,
      default: 'compact',
      validator: value => ['compact', 'comfortable', 'dense'].includes(value)
    },
    skeletonRows: {
      type: Number,
      default: 8
    },
    loading: Boolean,
    clickable: Boolean,
    isRowExpanded: { type: Function, default: null },
    // The current order: { key: <column key>, direction: 'asc' | 'desc' }, or null for the default order.
    sort: {
      type: Object,
      default: null
    }
  },
  methods: {
    columnClass(column) {
      return [
        column.className,
        column.align ? `app-data-table__cell--${column.align}` : null,
        column.sticky ? 'app-data-table__cell--sticky' : null,
        column.stickyRight ? 'app-data-table__cell--sticky-right' : null
      ]
    },
    columnStyle(column) {
      return {
        width: column.width || null,
        minWidth: column.minWidth || null
      }
    },
    sortDirection(column) {
      return this.sort && this.sort.key === column.key ? this.sort.direction : null
    },
    ariaSort(column) {
      if (!column.sortable) return null
      const direction = this.sortDirection(column)
      if (!direction) return 'none'
      return direction === 'asc' ? 'ascending' : 'descending'
    },
    // A click cycles ascending, descending, and back to the default order (the event carries null).
    toggleSort(column) {
      const next = { null: 'asc', asc: 'desc', desc: null }[this.sortDirection(column)]
      this.$emit('sort', next ? { key: column.key, direction: next } : null)
    },
    // Frozen columns sit at the left edge; a second one is offset by the width of the first.
    cellStyle(column) {
      if (column.stickyRight) return { right: column.stickyRight === true ? 0 : column.stickyRight }
      return column.sticky && column.stickyLeft ? { left: column.stickyLeft } : null
    },
    displayValue(value) {
      return value === null || value === undefined || value === '' ? '—' : value
    },
    rowIdentity(row, index) {
      if (typeof this.rowKey === 'function') return this.rowKey(row)
      return row[this.rowKey] === undefined ? index : row[this.rowKey]
    },
    selectRowByKey(row, event) {
      if (!this.clickable || this.isInteractiveTarget(event.target)) return
      event.preventDefault()
      this.selectRow(row, event)
    },
    selectRow(row, event) {
      if (!this.clickable || this.isInteractiveTarget(event.target)) return
      this.$emit('row-click', { row, event })
    },
    isInteractiveTarget(target) {
      return Boolean(target && target.closest && target.closest('a, button, input, select, textarea, [role="button"]'))
    }
  }
}
</script>

<style lang="scss" scoped>
.app-data-table {
  position: relative;
  width: 100%;
  overflow-x: auto;
  color: $ui-text;
  font-family: $ui-font-body;

  table {
    width: 100%;
    margin: 0;
    border-collapse: collapse;
    border-spacing: 0;
    table-layout: fixed;
    background-color: $ui-surface;
  }

  th {
    box-sizing: border-box;
    height: 42px;
    padding: $ui-space-4 $ui-space-12;
    border-bottom: 1px solid $ui-line;
    background-color: $ui-surface-soft;
    color: $ui-muted;
    font-size: $ui-font-size-metadata;
    font-weight: 600;
    text-align: left;
    vertical-align: middle;
  }

  td {
    box-sizing: border-box;
    height: 46px;
    padding: $ui-space-4 $ui-space-12;
    border-bottom: 1px solid $ui-line;
    background-color: $ui-surface;
    color: $ui-text;
    font-size: $ui-font-size-body;
    text-align: left;
    vertical-align: middle;
  }

  :deep(a), :deep(.ui-link) {
    font-weight: 500;
  }

  tbody tr:nth-child(even) td {
    background-color: $ui-surface-soft;
  }

  tbody tr:last-child td {
    border-bottom: 0;
  }

  tbody tr.app-data-table__row--clickable {
    cursor: pointer;
  }

  tbody tr.app-data-table__row--clickable:hover td,
  tbody tr.app-data-table__row--clickable:focus-visible td {
    background-color: var(--ui-accent-soft, #{$ui-blue-soft});
  }

  tbody tr:focus-visible {
    outline: 3px solid var(--ui-accent-soft, #{rgba($ui-blue, .18)});
    outline-offset: -3px;
  }
}

.app-data-table--comfortable {
  th { height: 48px; padding: $ui-space-8 $ui-space-12; }
  td { height: 54px; padding: $ui-space-8 $ui-space-12; }
}

.app-data-table--dense {
  th, td { padding: $ui-space-4 $ui-space-8; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  th { color: $ui-ink; font-weight: 700; }
  // Leave room for borders around 30px actions within a 38px row.
  td { height: 38px; padding-top: $ui-space-4 - 1px; padding-bottom: $ui-space-4 - 1px; }
}

.app-data-table__detail-row td {
  height: auto;
  padding: $ui-space-16;
  white-space: normal;
  overflow: visible;
}

.app-data-table__sort {
  display: inline-flex;
  align-items: center;
  gap: $ui-space-4;
  max-width: 100%;
  padding: 0;
  border: 0;
  background: none;
  color: inherit;
  cursor: pointer;
  font: inherit;
  text-align: inherit;

  &:hover,
  &--active { color: $ui-text; }

  &:focus-visible {
    border-radius: $ui-control-radius;
    outline: 3px solid var(--ui-accent-soft, #{rgba($ui-blue, .18)});
    outline-offset: 2px;
  }
}

// Two small triangles; the one that matches the current direction turns blue.
.app-data-table__sort-icon {
  position: relative;
  flex: 0 0 auto;
  width: 8px;
  height: 12px;

  &::before,
  &::after {
    content: '';
    position: absolute;
    left: 0;
    border-right: 4px solid transparent;
    border-left: 4px solid transparent;
  }

  &::before { top: 0; border-bottom: 5px solid $ui-line-strong; }
  &::after { bottom: 0; border-top: 5px solid $ui-line-strong; }
  &--asc::before { border-bottom-color: var(--ui-accent, #{$ui-blue}); }
  &--desc::after { border-top-color: var(--ui-accent, #{$ui-blue}); }
}

.app-data-table--refreshing table {
  opacity: .55;
}

.app-data-table__skeleton {
  display: block;
  width: 80%;
  max-width: 220px;
  height: 14px;
  border-radius: $ui-control-radius;
  animation: app-data-table-skeleton 1.3s ease-in-out infinite;
  background-color: $ui-line;
}

// A frozen first column keeps the row identifiable while a wide table scrolls sideways.
// The cell backgrounds are set on td, so zebra and hover colours stay opaque over the scrolled cells.
.app-data-table__cell--sticky {
  position: sticky;
  left: 0;
  z-index: 1;
  box-shadow: 1px 0 0 $ui-line;
}

th.app-data-table__cell--sticky {
  z-index: 2;
}

.app-data-table__cell--sticky-right {
  position: sticky;
  right: 0;
  z-index: 1;
  box-shadow: -1px 0 0 $ui-line;
}

th.app-data-table__cell--sticky-right {
  z-index: 2;
}

.app-data-table__cell--right {
  text-align: right;
}

.app-data-table__cell--center {
  text-align: center;
}

.app-data-table__ellipsis {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

@keyframes app-data-table-skeleton {
  50% { opacity: .45; }
}

@media (prefers-reduced-motion: reduce) {
  .app-data-table__skeleton { animation: none; }
}
</style>
